import CommentPolicyHint from "../components/CommentPolicyHint";
import { usePermission } from "../hooks/usePermission";
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import {
  Plus, X, Users, Clock, ListTodo, Filter, Search,
  Pencil, Trash2, GripVertical,
  LayoutGrid, Kanban, Play, Square, CheckSquare,
} from "lucide-react";

/* ─── Column definitions ──────────────────────────────────────── */
const COLUMNS = [
  { key: "PLANNING",    label: "Planning",    color: "bg-purple-500", light: "bg-purple-50",  border: "border-purple-200" },
  { key: "IN_PROGRESS", label: "In Progress", color: "bg-blue-500",   light: "bg-blue-50",    border: "border-blue-200"   },
  { key: "ON_HOLD",     label: "On Hold",     color: "bg-orange-500", light: "bg-orange-50",  border: "border-orange-200" },
  { key: "COMPLETED",   label: "Completed",   color: "bg-green-500",  light: "bg-green-50",   border: "border-green-200"  },
];

const emptyForm = {
  name: "", clientName: "", description: "", department: "",
  startDate: "", endDate: "", status: "PLANNING",
  budget: "", estimatedHours: "", hourlyRate: "",
  memberIds: [] as string[],
};

const blankLog = { minutes: "", unit: "min", date: new Date().toISOString().slice(0, 10), comment: "" };

const fmt = (sec: number) => {
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60 > 0 ? (m % 60) + "m" : ""}`.trim();
};
const fmtH = (hours: number) => {
  const m = Math.round((hours || 0) * 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
};

// Parses flexible time input: "15" (min), "1.5" (hr), "1h 30m", "90m"
const parseToMinutes = (val: string, unit: string): number => {
  const s = (val || "").trim();
  // "1h 30m" or "1h30m"
  const hm = s.match(/^(\d+(?:\.\d+)?)h\s*(\d+)m?$/i);
  if (hm) return Math.max(1, Math.round(parseFloat(hm[1]) * 60 + parseInt(hm[2])));
  // "1.5h" or "2h"
  const h = s.match(/^(\d+(?:\.\d+)?)h$/i);
  if (h) return Math.max(1, Math.round(parseFloat(h[1]) * 60));
  // "30m"
  const m = s.match(/^(\d+)m$/i);
  if (m) return Math.max(1, parseInt(m[1]));
  // plain number — use selected unit
  const n = parseFloat(s);
  if (isNaN(n) || n <= 0) return 0;
  return unit === "hr" ? Math.max(1, Math.round(n * 60)) : Math.max(1, Math.round(n));
};
const fmtMin = (m: number) => { const h = Math.floor(m/60); return `${h}:${String(m%60).padStart(2,"0")}`; };

/* ─── Component ──────────────────────────────────────────────── */
const Projects: React.FC = () => {
  const navigate = useNavigate();
  
  const canCreate = usePermission("projects", "create");
  const canEdit   = usePermission("projects", "edit");
  const canDelete = usePermission("projects", "delete");
  const [projects, setProjects]             = useState<any[]>([]);
  const [projSearch,   setProjSearch]   = useState("");
  const [projStatus,   setProjStatus]   = useState("");
  const [projClient,   setProjClient]   = useState("");
  const [projDept,     setProjDept]     = useState("");
  const [projSize,     setProjSize]     = useState("");
  const [projDateFrom, setProjDateFrom] = useState("");
  const [projDateTo,   setProjDateTo]   = useState("");
  const [projMember,   setProjMember]   = useState("");
  const [projFilters,  setProjFilters]  = useState(false);
  const [users, setUsers]                   = useState<any[]>([]);
  const [showForm, setShowForm]             = useState(false);
  const [editingProject, setEditingProject] = useState<any>(null);
  const [form, setForm]                     = useState({ ...emptyForm });
  const [viewMode, setViewMode]             = useState<"kanban" | "grid" | "steps">("kanban");
  const [logProject, setLogProject]         = useState<any>(null);
  const [logForm, setLogForm]               = useState({ ...blankLog });
  const [tick, setTick]                     = useState(0);

  const dragId    = useRef<string | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);

  /* ── live timer ───────────────────────────────────────────── */
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  /* ── load ─────────────────────────────────────────────────── */
  const load = () => api.get("/projects").then(r => setProjects(r.data));
  const clientOptions = React.useMemo(() =>
    Array.from(new Set((projects || []).map((p: any) => p.clientName).filter(Boolean))).sort(),
    [projects]
  );
  const deptOptions = React.useMemo(() =>
    Array.from(new Set((projects || []).map((p: any) => p.department).filter(Boolean))).sort(),
    [projects]
  );

  const visibleProjects = React.useMemo(() => {
    const q = projSearch.trim().toLowerCase();
    return (projects || []).filter((p: any) => {
      if (q) {
        const hit = (p.name||"").toLowerCase().includes(q)
                 || (p.clientName||"").toLowerCase().includes(q)
                 || (p.description||"").toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (projStatus && p.status !== projStatus) return false;
      if (projClient && p.clientName !== projClient) return false;
      if (projDept   && p.department !== projDept) return false;
      if (projSize   && (p.projectSize || "MEDIUM") !== projSize) return false;
      if (projDateFrom) {
        const d = p.startDate ? new Date(p.startDate) : (p.createdAt ? new Date(p.createdAt) : null);
        if (d && d < new Date(projDateFrom)) return false;
      }
      if (projDateTo) {
        const d = p.startDate ? new Date(p.startDate) : (p.createdAt ? new Date(p.createdAt) : null);
        if (d && d > new Date(projDateTo + "T23:59:59")) return false;
      }
      if (projMember) {
        const isMember = (p.members || []).some((m: any) =>
          m.userId === projMember || m.user?.id === projMember
        );
        if (!isMember) return false;
      }
      return true;
    });
  }, [projects, projSearch, projStatus, projClient, projDept, projSize, projDateFrom, projDateTo, projMember]);

  const resetProjFilters = () => {
    setProjSearch(""); setProjStatus(""); setProjClient(""); setProjDept("");
    setProjSize(""); setProjDateFrom(""); setProjDateTo(""); setProjMember("");
  };

  useEffect(() => {
    load();
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
  }, []);

  /* ── open create modal — always reset form ──────────────── */
  const openCreateModal = () => {
    setEditingProject(null);
    setForm({ ...emptyForm });
    setShowForm(true);
  };

  /* ── helpers ──────────────────────────────────────────────── */
  const toggleMember = (uid: string) =>
    setForm(f => ({
      ...f,
      memberIds: f.memberIds.includes(uid)
        ? f.memberIds.filter(id => id !== uid)
        : [...f.memberIds, uid],
    }));

  const openCreate = () => {
    setEditingProject(null);
    setForm({ ...emptyForm });
    setShowForm(true);
  };

  const openEdit = (e: React.MouseEvent, p: any) => {
    e.stopPropagation();
    setEditingProject(p);
    setForm({
      name: p.name || "",
      clientName: p.clientName || "",
      description: p.description || "",
      department: p.department || "",
      startDate: p.startDate ? p.startDate.slice(0, 10) : "",
      endDate: p.endDate ? p.endDate.slice(0, 10) : "",
      status: p.status || "PLANNING",
      budget: p.budget?.toString() || "",
      estimatedHours: p.estimatedHours?.toString() || "",
      hourlyRate: p.hourlyRate?.toString() || "",
      memberIds: p.members?.map((m: any) => m.userId || m.id) || [],
    });
    setShowForm(true);
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!window.confirm("Delete this project? This cannot be undone.")) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      load();
    } catch { toast.error("Failed to delete project"); }
  };

  const startTimer = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try { await api.post(`/projects/${id}/start-timer`); toast.success("Timer started!"); load(); }
    catch { toast.error("Failed to start timer"); }
  };

  const stopTimer = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try { await api.post(`/projects/${id}/stop-timer`); toast.success("Timer stopped!"); load(); }
    catch { toast.error("Failed to stop timer"); }
  };

  const openLog = (e: React.MouseEvent, p: any) => {
    e.stopPropagation();
    setLogProject(p);
    setLogForm({ ...blankLog });
  };

  const submitLog = async (ev: React.FormEvent) => {
    ev.preventDefault();
    const _mins = parseToMinutes(logForm.minutes, logForm.unit || "min"); if (!_mins || _mins < 1) { toast.error("Minimum 1 minute"); return; }
    try {
      const _pc = await api.get(`/comment-policy/check?minutes=${_mins}`);
      const _v: any = _pc.data || {};
      const _trim = (logForm.comment || "").trim();
      if (_v.required && (!_trim || _trim.length < (_v.minLength || 0))) {
        toast.error(`Comment of ${_v.minLength}+ characters required for ${_mins} min`); return;
      }
      if (!_v.required && _v.recommended && !_trim) {
        if (!window.confirm(`You worked ${_mins} minutes — a short comment is recommended. Save without one?`)) return;
      }
    } catch {}
    try {
      await api.post(`/projects/${logProject.id}/worklogs`, {
        minutes: _mins,
        date: logForm.date,
        comment: logForm.comment,
      });
      toast.success("Time logged!");
      setLogProject(null);
      load();
    } catch (err: any) { toast.error(err?.response?.data?.message || "Failed to log time"); }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...form,
      budget: form.budget ? +form.budget : 0,
      estimatedHours: form.estimatedHours ? +form.estimatedHours : 0,
      hourlyRate: form.hourlyRate ? +form.hourlyRate : 0,
    };
    try {
      if (editingProject) {
        await api.put(`/projects/${editingProject.id}`, payload);
        toast.success("Project updated!");
      } else {
        await api.post("/projects", payload);
        toast.success("Project created!");
      }
      setShowForm(false);
      setEditingProject(null);
      setForm({ ...emptyForm });
      load();
    } catch { toast.error(editingProject ? "Failed to update" : "Failed to create"); }
  };

  /* ── drag & drop ──────────────────────────────────────────── */
  const onDragStart = (e: React.DragEvent, id: string) => { dragId.current = id; e.dataTransfer.effectAllowed = "move"; };
  const onDragOver  = (e: React.DragEvent, colKey: string) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOver(colKey); };
  const onDrop = async (e: React.DragEvent, colKey: string) => {
    e.preventDefault(); setDragOver(null);
    const id = dragId.current; if (!id) return;
    const project = projects.find(p => p.id === id);
    if (!project || project.status === colKey) return;
    setProjects(prev => prev.map(p => p.id === id ? { ...p, status: colKey } : p));
    try {
      await api.put(`/projects/${id}`, { status: colKey });
      toast.success(`Moved to ${COLUMNS.find(c => c.key === colKey)?.label}`);
    } catch { toast.error("Failed to update status"); load(); }
    dragId.current = null;
  };
  const onDragEnd = () => { setDragOver(null); dragId.current = null; };

  const cardProps = (p: any) => ({
    project: p, tick,
    onNavigate:   () => navigate(`/projects/${p.id}`),
    onEdit:       (e: React.MouseEvent) => openEdit(e, p),
    onDelete:     (e: React.MouseEvent) => handleDelete(e, p.id),
    onStartTimer: (e: React.MouseEvent) => startTimer(e, p.id),
    onStopTimer:  (e: React.MouseEvent) => stopTimer(e, p.id),
    onLogTime:    (e: React.MouseEvent) => openLog(e, p),
  });

  /* ── render ───────────────────────────────────────────────── */
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 shrink-0">
        <h2 className="text-2xl font-bold text-gray-900">Projects</h2>
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-gray-100 rounded-lg p-1 gap-1">
            <button onClick={() => setViewMode("kanban")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === "kanban" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              <Kanban size={14} /> Kanban
            </button>
            <button onClick={() => setViewMode("grid")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === "grid" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              <LayoutGrid size={14} /> Grid
            </button>
            <button onClick={() => setViewMode("steps")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === "steps" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              <CheckSquare size={14} /> Steps
            </button>
          </div>
          <button onClick={openCreate}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">
            <Plus size={16} /> New Project
          </button>
        </div>
      </div>

      {/* ── Kanban View ───────────────────────────────────────── */}
      {/* ── Filter Bar ─────────────────────────── */}
      <div className="mb-4 bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2">
          <Search size={14} className="text-gray-400"/>
          <input
            type="text"
            value={projSearch}
            onChange={e => setProjSearch(e.target.value)}
            placeholder="Search projects by name, client, or description..."
            className="flex-1 border-0 focus:outline-none text-sm"
          />
          <button
            type="button"
            onClick={() => setProjFilters(!projFilters)}
            className="flex items-center gap-1 px-3 py-1 text-xs border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <Filter size={12}/>
            Filters
            {(projStatus || projClient || projDept || projSize || projDateFrom || projDateTo || projMember) && (
              <span className="bg-blue-100 text-blue-700 rounded-full px-1.5 py-0.5 ml-1">Active</span>
            )}
          </button>
        </div>
        {projFilters && (
          <div className="px-4 pb-3 pt-1 border-t border-gray-100 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            <select value={projStatus} onChange={e => setProjStatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs">
              <option value="">All Statuses</option>
              {COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <select value={projClient} onChange={e => setProjClient(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs">
              <option value="">All Clients</option>
              {clientOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={projDept} onChange={e => setProjDept(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs">
              <option value="">All Departments</option>
              {deptOptions.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={projSize} onChange={e => setProjSize(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs">
              <option value="">All Sizes</option>
              <option value="SMALL">🟢 Small</option>
              <option value="MEDIUM">🟡 Medium</option>
              <option value="LARGE">🔴 Large</option>
            </select>
            <input type="date" value={projDateFrom} onChange={e => setProjDateFrom(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs"/>
            <input type="date" value={projDateTo} onChange={e => setProjDateTo(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs"/>
            <select value={projMember} onChange={e => setProjMember(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs col-span-2">
              <option value="">All Team Members</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}{u.department ? ` — ${u.department}` : ""}</option>)}
            </select>
            <div className="col-span-2 md:col-span-3 lg:col-span-6 flex justify-between text-xs">
              <span className="text-gray-500">Showing {visibleProjects.length} of {projects.length} projects</span>
              <button type="button" onClick={resetProjFilters}
                className="text-gray-500 hover:text-gray-700 px-3 py-1 rounded border border-gray-200 hover:bg-gray-50">
                Reset all filters
              </button>
            </div>
          </div>
        )}
      </div>

      {viewMode === "kanban" && (
        <div className="flex gap-4 overflow-x-auto pb-4 flex-1 min-h-0">
          {COLUMNS.map(col => {
            const colProjects = visibleProjects.filter(p => p.status === col.key);
            const isOver = dragOver === col.key;
            return (
              <div key={col.key} className="flex flex-col shrink-0 w-72"
                onDragOver={e => onDragOver(e, col.key)}
                onDrop={e => onDrop(e, col.key)}
                onDragLeave={() => setDragOver(null)}>
                <div className={`flex items-center justify-between px-3 py-2.5 rounded-t-xl border ${col.border} ${col.light}`}>
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${col.color}`} />
                    <span className="font-semibold text-gray-700 text-sm">{col.label}</span>
                    <span className="bg-white text-gray-500 text-xs font-medium px-2 py-0.5 rounded-full border border-gray-200">{colProjects.length}</span>
                  </div>
                </div>
                <div className={`flex-1 rounded-b-xl border-x border-b p-2 space-y-2 min-h-[200px] transition-colors ${col.border} ${isOver ? col.light + " ring-2 ring-inset ring-blue-400" : "bg-gray-50/70"}`}>
                  {colProjects.map(p => (
                    <ProjectCard key={p.id} {...cardProps(p)}
                      onDragStart={e => onDragStart(e, p.id)}
                      onDragEnd={onDragEnd} />
                  ))}
                  {colProjects.length === 0 && (
                    <div className={`h-24 rounded-lg border-2 border-dashed flex items-center justify-center text-xs text-gray-400 ${isOver ? "border-blue-400 bg-blue-50" : "border-gray-200"}`}>
                      {isOver ? "Drop here" : "No projects"}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Grid View ─────────────────────────────────────────── */}
      {viewMode === "grid" && (
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 overflow-y-auto pb-4">
          {visibleProjects.map(p => (
            <ProjectCard key={p.id} {...cardProps(p)} onDragStart={() => {}} onDragEnd={() => {}} />
          ))}
          {visibleProjects.length === 0 && (
            <div className="col-span-4 text-center py-16 text-gray-400 bg-white rounded-xl border border-gray-200">
              No projects yet. Create your first project!
            </div>
          )}
        </div>
      )}

      {/* ── Steps Review ──────────────────────────────────────── */}
      {viewMode === "steps" && <StepsReview projects={projects} users={users} />}

      {/* ══ Create / Edit Modal ═══════════════════════════════════ */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">{editingProject ? "Edit Project" : "Create New Project"}</h3>
              <button onClick={() => { setShowForm(false); setEditingProject(null); }} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-5 max-h-[80vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="Cloud Migration Project"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                  <input value={form.clientName} onChange={e => setForm({ ...form, clientName: e.target.value })} placeholder="Acme Corp"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Project description..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input type="date" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                    {["PLANNING", "IN_PROGRESS", "ON_HOLD", "COMPLETED", "CANCELLED"].map(s => (
                      <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Budget ($)</label>
                  <input type="number" value={form.budget} onChange={e => setForm({ ...form, budget: e.target.value })} placeholder="50000"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Hours</label>
                  <input type="number" value={form.estimatedHours} onChange={e => setForm({ ...form, estimatedHours: e.target.value })} placeholder="100"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate ($)</label>
                  <input type="number" value={form.hourlyRate} onChange={e => setForm({ ...form, hourlyRate: e.target.value })} placeholder="50"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Team Members</label>
                <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto space-y-1">
                  {users.map(u => (
                    <label key={u.id} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg">
                      <input type="checkbox" checked={form.memberIds.includes(u.id)} onChange={() => toggleMember(u.id)} className="w-4 h-4 text-blue-600 rounded" />
                      <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">{u.name[0]}</div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{u.name}</p>
                        <p className="text-xs text-gray-500">{u.department || u.role}</p>
                      </div>
                    </label>
                  ))}
                  {users.length === 0 && <p className="text-sm text-gray-400 text-center py-2">No users</p>}
                </div>
                {form.memberIds.length > 0 && <p className="text-xs text-blue-600 mt-1">{form.memberIds.length} member(s) selected</p>}
              </div>
              <div className="flex gap-3 pt-2 border-t border-gray-200">
                <button type="button" onClick={() => { setShowForm(false); setEditingProject(null); }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg hover:bg-gray-50 text-sm font-medium">Cancel</button>
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg hover:bg-blue-700 text-sm font-medium">
                  {editingProject ? "Save Changes" : "Create Project"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══ Log Time Modal ════════════════════════════════════════ */}
      {logProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Log Time</h3>
                <p className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">{logProject.name}</p>
              </div>
              <button onClick={() => setLogProject(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={submitLog} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time Logged *</label>
                <div className="flex gap-2">
                  <input type="text" value={logForm.minutes}
                    onChange={e => setLogForm(f => ({ ...f, minutes: e.target.value }))}
                    required placeholder={logForm.unit==="hr" ? "e.g. 1.5" : "e.g. 30"}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                  <div className="flex border border-gray-300 rounded-lg overflow-hidden text-sm">
                    <button type="button" onClick={() => setLogForm(f=>({...f,unit:"min"}))}
                      className={`px-3 py-2 font-medium transition-colors ${(logForm.unit||"min")==="min"?"bg-blue-600 text-white":"bg-white text-gray-600 hover:bg-gray-50"}`}>min</button>
                    <button type="button" onClick={() => setLogForm(f=>({...f,unit:"hr"}))}
                      className={`px-3 py-2 font-medium transition-colors ${logForm.unit==="hr"?"bg-blue-600 text-white":"bg-white text-gray-600 hover:bg-gray-50"}`}>hr</button>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1">Also accepts: 1h 30m · 1.5 · 90</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input type="date" value={logForm.date} onChange={e => setLogForm(f => ({ ...f, date: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</label>
                <textarea value={logForm.comment} onChange={e => setLogForm(f => ({ ...f, comment: e.target.value }))}
                  rows={2} placeholder="What did you work on?"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div className="flex gap-3 pt-1 border-t border-gray-100">
                <button type="button" onClick={() => setLogProject(null)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">Log Time</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Project Card ────────────────────────────────────────────── */
interface CardProps {
  project: any;
  tick: number;
  onNavigate: () => void;
  onEdit: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  onStartTimer: (e: React.MouseEvent) => void;
  onStopTimer: (e: React.MouseEvent) => void;
  onLogTime: (e: React.MouseEvent) => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}

function ProjectCard({ project: p, tick, onNavigate, onEdit, onDelete, onStartTimer, onStopTimer, onLogTime, onDragStart, onDragEnd }: CardProps) {
  const pct = p.estimatedHours > 0
    ? Math.min(100, Math.round((p.totalLoggedMinutes / ((p.estimatedHours||0)*60||1)) * 100))
    : 0;

  const liveElapsed = p.timerStart
    ? Math.floor((Date.now() - new Date(p.timerStart).getTime()) / 1000)
    : 0;
  const totalSec = (p.timerTotal || 0) + liveElapsed;

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onNavigate}
      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all cursor-grab active:cursor-grabbing active:opacity-70 active:scale-95 select-none"
    >
      {/* Title */}
      <div className="flex items-start gap-2 px-3 pt-3 pb-1">
        <GripVertical size={14} className="text-gray-300 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-800 text-sm leading-tight truncate">{p.name}</h3>
            {(p as any).projectSize && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold shrink-0 ${
                (p as any).projectSize === "LARGE"  ? "bg-red-100 text-red-700" :
                (p as any).projectSize === "MEDIUM" ? "bg-yellow-100 text-yellow-700" :
                "bg-green-100 text-green-700"
              }`}>
                {(p as any).projectSize === "LARGE"  ? "Large"  :
                 (p as any).projectSize === "MEDIUM" ? "Medium" : "Small"}
              </span>
            )}
          </div>
          {p.clientName && <p className="text-xs text-gray-400 mt-0.5 truncate">{p.clientName}</p>}
        </div>
      </div>

      {/* Description */}
      {p.description && (
        <p className="text-xs text-gray-400 px-3 pb-1 line-clamp-2 leading-relaxed">{p.description}</p>
      )}

      {/* Stats */}
      <div className="flex items-center gap-3 px-3 pb-1 text-xs text-gray-500">
        <span className={`flex items-center gap-1 font-mono ${p.timerStart ? "text-green-600 font-semibold" : ""}`}>
          <Clock size={10} />{fmt(totalSec)}
        </span>
        <span className="ml-auto flex items-center gap-1"><Users size={10} />{p.members?.length || 0}</span>
        <span className="flex items-center gap-1"><ListTodo size={10} />{p.tasks?.length || 0}</span>
      </div>

      {/* Hours Row */}
      <div className="flex items-center gap-2 px-3 pb-1">
        <div className="flex items-center gap-1.5 bg-orange-50 border border-orange-100 rounded-md px-2 py-1 flex-1">
          <Clock size={11} className="text-orange-400 flex-shrink-0" />
          <span className="text-[11px] text-gray-500">Estimated:</span>
          <span className="text-xs font-bold text-orange-600 ml-auto">{p.estimatedHours || 0}h</span>
        </div>
        <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-100 rounded-md px-2 py-1 flex-1">
          <Clock size={11} className="text-blue-400 flex-shrink-0" />
          <span className="text-[11px] text-gray-500">Logged:</span>
          <span className="text-xs font-bold text-blue-600 ml-auto">{fmtMin(p.totalLoggedMinutes)}</span>
        </div>
      </div>

      {/* Progress */}
      <div className="px-3 pb-2">
        <div className="w-full bg-gray-100 rounded-full h-1.5">
          <div className={`h-1.5 rounded-full transition-all ${pct >= 100 ? "bg-green-500" : "bg-blue-500"}`} style={{ width: `${pct}%` }} />
        </div>
        <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
          <span>{pct}% complete</span>
          <span>{fmtMin(p.totalLoggedMinutes||0)} / {fmtMin((p.estimatedHours||0)*60)}</span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-1 px-3 pb-3" onClick={e => e.stopPropagation()}>
        <button onClick={onLogTime}
          className="flex items-center gap-0.5 text-[10px] bg-blue-50 text-blue-600 border border-blue-200 px-1.5 py-0.5 rounded-md hover:bg-blue-100 font-medium">
          <Clock size={9} /> Log Time
        </button>
        <button onClick={onEdit}
          className="flex items-center gap-0.5 text-[10px] bg-gray-50 text-gray-600 border border-gray-200 px-1.5 py-0.5 rounded-md hover:bg-gray-100 font-medium">
          <Pencil size={9} /> Edit
        </button>
        <button onClick={onDelete}
          className="flex items-center gap-0.5 text-[10px] bg-red-50 text-red-500 border border-red-100 px-1.5 py-0.5 rounded-md hover:bg-red-100 font-medium">
          <Trash2 size={9} /> Delete
        </button>
      </div>
    </div>
  );
}

/* ─────────── Steps Review Sub-Tab ─────────── */
function firstOfMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function lastOfMonth(): string {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, "0")}-${String(last.getDate()).padStart(2, "0")}`;
}

const STEP_LEVEL_META: Record<string, { color: string; bg: string; emoji: string; label: string }> = {
  simple:   { color: "text-green-700",  bg: "bg-green-50 border-green-200",   emoji: "🟢", label: "Simple" },
  medium:   { color: "text-yellow-700", bg: "bg-yellow-50 border-yellow-200", emoji: "🟡", label: "Medium" },
  critical: { color: "text-red-700",    bg: "bg-red-50 border-red-200",       emoji: "🔴", label: "Critical" },
};

const STEP_STATUS_COLOR: Record<string, string> = {
  OPEN:        "bg-gray-50 text-gray-700",
  IN_PROGRESS: "bg-blue-50 text-blue-700",
  ON_HOLD:     "bg-amber-50 text-amber-700",
  COMPLETED:   "bg-green-50 text-green-700",
};

function StepsReview({ projects, users }: { projects: any[]; users: any[] }) {
  const { user } = useAuth();
  const canClassify = user?.role === "ADMIN" || user?.role === "MANAGER" || user?.role === "DEPARTMENT_HEAD";

  const [from, setFrom]           = useState(firstOfMonth());
  const [to, setTo]               = useState(lastOfMonth());
  const [projectId, setProjectId] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [status, setStatus]       = useState("");
  const [classified, setClassified] = useState("");
  const [level, setLevel]         = useState("");

  const [rows, setRows]           = useState<any[]>([]);
  const [summary, setSummary]     = useState<any>(null);
  const [loading, setLoading]     = useState(true);
  const [classifying, setClassifying] = useState<any>(null);
  const [saving, setSaving]       = useState(false);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to)   params.set("to", to);
    if (projectId) params.set("projectId", projectId);
    if (assigneeId) params.set("assigneeId", assigneeId);
    if (status) params.set("status", status);
    if (classified) params.set("classified", classified);
    if (level) params.set("workLevel", level);
    api.get(`/projects/steps-review?${params.toString()}`)
      .then(r => { setRows(r.data.rows || []); setSummary(r.data.summary || null); })
      .catch(() => { setRows([]); setSummary(null); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [from, to, projectId, assigneeId, status, classified, level]);

  const doClassify = async (task: any, newLevel: "simple" | "medium" | "critical") => {
    setSaving(true);
    try {
      await api.put(`/tasks/${task.id}/classify`, { workLevel: newLevel });
      toast.success("Classified");
      setClassifying(null);
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setFrom(firstOfMonth()); setTo(lastOfMonth());
    setProjectId(""); setAssigneeId(""); setStatus(""); setClassified(""); setLevel("");
  };

  return (
    <div className="space-y-4">
      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-white border border-gray-200 rounded-xl p-3">
            <div className="text-xs text-gray-500">Total Steps</div>
            <div className="text-xl font-bold text-gray-900">{summary.total}</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-xl p-3">
            <div className="text-xs text-green-700">Classified</div>
            <div className="text-xl font-bold text-green-800">{summary.classified}</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <div className="text-xs text-amber-700">Not Classified</div>
            <div className="text-xl font-bold text-amber-800">{summary.notClassified}</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
            <div className="text-xs text-blue-700">Completed</div>
            <div className="text-xl font-bold text-blue-800">{summary.completed}</div>
          </div>
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3">
            <div className="text-xs text-indigo-700">Total Points</div>
            <div className="text-xl font-bold text-indigo-800">{summary.totalPoints}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-3">
        <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs"/>
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs"/>
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Project</label>
            <select value={projectId} onChange={e => setProjectId(e.target.value)} className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs">
              <option value="">All</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Assignee</label>
            <select value={assigneeId} onChange={e => setAssigneeId(e.target.value)} className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs">
              <option value="">All</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs">
              <option value="">All</option>
              <option value="OPEN">Open</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="ON_HOLD">On Hold</option>
              <option value="COMPLETED">Completed</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Classification</label>
            <select value={classified} onChange={e => setClassified(e.target.value)} className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs">
              <option value="">All</option>
              <option value="no">Not Classified</option>
              <option value="yes">Classified</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Level</label>
            <select value={level} onChange={e => setLevel(e.target.value)} className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs">
              <option value="">All</option>
              <option value="simple">🟢 Simple</option>
              <option value="medium">🟡 Medium</option>
              <option value="critical">🔴 Critical</option>
            </select>
          </div>
        </div>
        <div className="mt-2 text-right">
          <button onClick={reset} className="text-xs text-blue-600 hover:underline">Reset filters</button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="p-6 text-gray-500">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-400">
          No steps match these filters.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Step</th>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Project</th>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Assignee</th>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Status</th>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Completed</th>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Level</th>
                {canClassify && <th className="text-left px-3 py-2 font-medium text-gray-600">Action</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r: any) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 max-w-xs truncate text-gray-800" title={r.title}>{r.title}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{r.project?.name || "—"}</td>
                  <td className="px-3 py-2 text-xs text-gray-700">{r.assignedTo?.name || <span className="text-gray-300">—</span>}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-md font-medium ${STEP_STATUS_COLOR[r.status] || "bg-gray-50 text-gray-600"}`}>{r.status}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                    {r.completedAt ? new Date(r.completedAt).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-2">
                    {r.workLevel && STEP_LEVEL_META[r.workLevel] ? (
                      <span className={`text-[11px] px-2 py-0.5 rounded-md font-medium border ${STEP_LEVEL_META[r.workLevel].bg} ${STEP_LEVEL_META[r.workLevel].color}`}>
                        {STEP_LEVEL_META[r.workLevel].emoji} {STEP_LEVEL_META[r.workLevel].label} · {r.workPoints}pt
                      </span>
                    ) : <span className="text-xs text-gray-400 italic">not classified</span>}
                  </td>
                  {canClassify && (
                    <td className="px-3 py-2">
                      <button onClick={() => setClassifying(r)}
                        className="text-xs px-2 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-md hover:bg-indigo-100">
                        {r.workLevel ? "Change" : "Classify"}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Classify Modal */}
      {classifying && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setClassifying(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b bg-indigo-50">
              <div className="text-lg font-semibold text-indigo-800">Classify Step</div>
              <div className="text-xs text-indigo-600 mt-1 truncate">{classifying.title}</div>
              <div className="text-[11px] text-indigo-500 mt-0.5">{classifying.project?.name}</div>
            </div>
            <div className="p-5 space-y-2">
              {(["simple","medium","critical"] as const).map(lv => {
                const m = STEP_LEVEL_META[lv];
                const points = lv === "simple" ? 1 : lv === "medium" ? 3 : 5;
                return (
                  <button key={lv} disabled={saving} onClick={() => doClassify(classifying, lv)}
                    className={`w-full text-left px-4 py-3 rounded-lg border transition-all hover:brightness-95 ${m.bg} ${m.color} ${classifying.workLevel === lv ? "ring-2 ring-offset-1 ring-indigo-400" : ""}`}>
                    <div className="font-semibold">{m.emoji} {m.label}</div>
                    <div className="text-xs opacity-70">{points} point{points === 1 ? "" : "s"}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Projects;
